import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import type { AssistantSurface } from "./layout";

interface Props {
  header: React.ReactNode;
  // Alerts and generation notices shown above the note.
  notices?: React.ReactNode;
  note: React.ReactNode;
  transcript: React.ReactNode;
  // Rendered once, in the panel, drawer or sheet depending on the width.
  assistant: React.ReactNode;
  surface: AssistantSurface;
  // Phones: the transcript renders its own docked bar, outside the page
  // flow.
  transcriptDocked?: boolean;
}

// Single-page visit workspace: header, the note as the main area and the
// transcript bar in one column; the assistant to the right on desktop, or in
// a drawer (tablet) or bottom sheet (phone).
export const VisitWorkspaceShell = ({
  header,
  notices,
  note,
  transcript,
  assistant,
  surface,
  transcriptDocked = false,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const isPanel = surface.placement === "panel";

  return (
    <div
      className="min-w-0 lg:absolute lg:inset-0 lg:flex"
      data-assistant-placement={surface.placement}
      data-testid="visit-workspace"
    >
      <div
        className="flex min-w-0 flex-1 flex-col lg:overflow-y-auto"
        id="visit-workspace-main"
      >
        <div className="mx-auto w-full max-w-[960px] flex-1 space-y-4 px-4 pb-4 pt-4 sm:px-6 sm:pt-6 lg:px-8">
          {header}
          {notices}
          {note}
        </div>
        {transcriptDocked ? (
          transcript
        ) : (
          <div
            className={cn(
              "mx-auto w-full max-w-[960px] px-4 pb-6 sm:px-6 lg:sticky lg:bottom-0 lg:z-10 lg:px-8 lg:pb-4",
              // With the panel collapsed the bar reaches the bottom-right
              // corner, where the floating Feedback button sits.
              isPanel && !surface.open && "lg:pr-44",
            )}
          >
            {transcript}
          </div>
        )}
      </div>

      {isPanel && surface.open && (
        <aside
          aria-label={t("visit.workspace.assistant")}
          className="flex w-[340px] shrink-0 flex-col overflow-y-auto border-l bg-grey-white pb-16 xl:w-[400px]"
          data-testid="assistant-panel"
        >
          {assistant}
        </aside>
      )}

      {!isPanel && (
        <Sheet open={surface.open} onOpenChange={surface.setOpen}>
          <SheetContent
            aria-describedby={undefined}
            className={cn(
              "flex flex-col overflow-y-auto p-0 [&_button]:min-h-11 [&_button.absolute]:w-11!",
              surface.placement === "sheet"
                ? "max-h-[88dvh] rounded-t-2xl"
                : "w-[420px] max-w-[90vw] sm:max-w-[420px]",
            )}
            data-testid="assistant-overlay"
            side={surface.placement === "sheet" ? "bottom" : "right"}
          >
            <SheetTitle className="sr-only">
              {t("visit.workspace.assistant")}
            </SheetTitle>
            {surface.placement === "sheet" && (
              <div
                aria-hidden
                className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-muted-foreground/30"
              />
            )}
            {assistant}
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
};
